import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  Building2,
  Globe,
  Loader2,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Plus,
  Search,
  Trash2,
  User,
} from 'lucide-react';
import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import { Card, CardContent } from '@/components/ui-kit/card';
import { Input } from '@/components/ui-kit/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { ConfirmationModal, RefreshButton } from '@/components/core';
import { useDebounce } from '@/hooks/use-debounce';
import { cn } from '@/lib/utils';
import { useGetOrganizationByOrgId } from '@/graphql/queries/organizations';
import { useDeleteCompany, useGetCompanies } from '../hooks/use-companies';
import { CompanyFormDialog } from '../components/company-form-dialog';
import { toWebsiteHref, toWebsiteLabel } from '../lib/website-url';
import type { Company, CompanyScope } from '../types/companies.types';

const matches = (company: Company, term: string) => {
  if (!term) return true;
  const q = term.toLowerCase();
  return (
    company.name.toLowerCase().includes(q) ||
    company.email.toLowerCase().includes(q) ||
    company.website.toLowerCase().includes(q) ||
    company.phone.toLowerCase().includes(q) ||
    company.address.toLowerCase().includes(q)
  );
};

export default function OrgCompaniesPage() {
  const { t } = useTranslation();
  const { orgId: slug } = useParams<{ orgId: string }>();
  const { data: org } = useGetOrganizationByOrgId(slug);
  const orgName = org?.Name ?? '';

  const { data: companies, isLoading } = useGetCompanies();
  const { mutate: deleteCompany } = useDeleteCompany();

  const [tab, setTab] = useState<CompanyScope>('user');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Company | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Company | null>(null);

  const all = useMemo(() => companies ?? [], [companies]);
  const counts = useMemo(
    () => ({
      user: all.filter((c) => c.scope === 'user').length,
      organization: all.filter((c) => c.scope === 'organization').length,
    }),
    [all]
  );

  const filtered = useMemo(
    () => all.filter((c) => c.scope === tab && matches(c, debouncedSearch.trim())),
    [all, tab, debouncedSearch]
  );

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (company: Company) => {
    setEditing(company);
    setDialogOpen(true);
  };

  const confirmDelete = () => {
    if (deleteTarget) deleteCompany(deleteTarget.id);
    setDeleteTarget(null);
  };

  return (
    <div className="space-y-6 font-nunito">
      {/* Header */}
      <div className="flex flex-col gap-1">
        <div className="flex justify-between items-center">
          <h1 className="text-lg md:text-2xl font-bold text-[hsl(var(--fg))] font-heading">
            {t('ORGCOMPANIES_TITLE')}
          </h1>

          <div className="flex shrink-0 items-center gap-3">
            <RefreshButton
              className="hidden md:flex"
              queryKey={['companies']}
              label={t('ORGTEAM_HEADER_REFRESH')}
            />
            <Button variant="primary" size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              {t('ORGCOMPANIES_ADD')}
            </Button>
          </div>
        </div>
        <p className="mt-1 text-globe-grey">{t('ORGCOMPANIES_SUBTITLE')}</p>
      </div>

      <Tabs value={tab} onValueChange={(value) => setTab(value as CompanyScope)}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <TabsList>
            <TabsTrigger value="user" className="gap-1.5 flex-1 md:flex-none">
              <User className="h-4 w-4" />
              {t('ORGCOMPANIES_TAB_MINE')}
              <Badge variant="secondary">{counts.user}</Badge>
            </TabsTrigger>
            <TabsTrigger value="organization" className="gap-1.5 flex-1 md:flex-none">
              <Building2 className="h-4 w-4" />
              {orgName || t('ORGCOMPANIES_TAB_ORG')}
              <Badge variant="secondary">{counts.organization}</Badge>
            </TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-2">
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-globe-grey" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('ORGCOMPANIES_SEARCH_PLACEHOLDER')}
                aria-label={t('ORGCOMPANIES_SEARCH_PLACEHOLDER')}
                className="h-9 pl-9"
              />
            </div>
            <RefreshButton
              className="md:hidden"
              queryKey={['companies']}
              label={t('ORGTEAM_HEADER_REFRESH')}
            />
          </div>
        </div>

        <TabsContent value={tab} className="mt-4">
          <CompanyList
            companies={filtered}
            isLoading={isLoading}
            scope={tab}
            orgName={orgName}
            onAdd={openCreate}
            onEdit={openEdit}
            onDelete={setDeleteTarget}
          />
        </TabsContent>
      </Tabs>

      <CompanyFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        company={editing}
        defaultShareWithOrg={!editing && tab === 'organization'}
        orgName={orgName}
      />

      <ConfirmationModal
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('ORGCOMPANIES_DELETE_CONFIRM_TITLE')}
        description={t('ORGCOMPANIES_DELETE_CONFIRM_DESCRIPTION', {
          name: deleteTarget?.name ?? '',
        })}
        onConfirm={confirmDelete}
        confirmText="DELETE"
        cancelText="CANCEL"
      />
    </div>
  );
}

interface CompanyListProps {
  companies: Company[];
  isLoading: boolean;
  scope: CompanyScope;
  orgName: string;
  onAdd: () => void;
  onEdit: (company: Company) => void;
  onDelete: (company: Company) => void;
}

function CompanyList({
  companies,
  isLoading,
  scope,
  orgName,
  onAdd,
  onEdit,
  onDelete,
}: CompanyListProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-globe-grey">
        <Loader2 className="h-5 w-5 animate-spin" />
        {t('ORGCOMPANIES_LOADING')}
      </div>
    );
  }

  if (companies.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <Building2 className="h-12 w-12 text-globe-grey-500 mb-4" />
          <p className="text-globe-grey mb-4">
            {scope === 'organization'
              ? t('ORGCOMPANIES_EMPTY_ORG', { org: orgName || t('ORGCOMPANIES_YOUR_ORG') })
              : t('ORGCOMPANIES_EMPTY_MINE')}
          </p>
          <Button variant="outline" size="sm" onClick={onAdd}>
            <Plus className="h-4 w-4" />
            {t('ORGCOMPANIES_ADD')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <ul className="overflow-hidden rounded-xl border border-border bg-background divide-y divide-border">
      {companies.map((company) => {
        const Icon = Building2;
        return (
          <li key={company.id} className="flex items-center gap-4 px-4 py-3">
            <div className="h-10 w-10 shrink-0 rounded-full bg-selise-blue/10 flex items-center justify-center">
              <Icon className="h-5 w-5 text-selise-blue" />
            </div>

            <div className="min-w-0 flex-1">
              <p className="truncate text-[15px] font-semibold text-[hsl(var(--fg))]">
                {company.name}
              </p>
              {company.address && (
                <p className="truncate text-sm text-globe-grey inline-flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5 shrink-0" />
                  {company.address}
                </p>
              )}
              <div className="mt-0.5 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-sm text-globe-grey">
                {company.website && (
                  <a
                    href={toWebsiteHref(company.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 hover:text-selise-blue hover:underline"
                  >
                    <Globe className="h-3.5 w-3.5" />
                    {toWebsiteLabel(company.website)}
                  </a>
                )}
                {company.email && (
                  <span className="inline-flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5" />
                    {company.email}
                  </span>
                )}
                {company.phone && (
                  <span className="inline-flex items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5" />
                    {company.phone}
                  </span>
                )}
              </div>
            </div>

            {company.canManage && (
              <div className={cn('flex shrink-0 items-center gap-1')}>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-globe-grey"
                  onClick={() => onEdit(company)}
                  aria-label={t('EDIT')}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-crimson hover:text-crimson"
                  onClick={() => onDelete(company)}
                  aria-label={t('DELETE')}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
